import React from 'react';
import PropTypes from 'prop-types';
import useAppContext from '../../context/useAppContext';
import { ToastContext, Table, TableBody, TableHeader, TableRow, TableHeaderCell, TableCell, RadioLabel, Form, Counter, Loader } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { Icon, Select } from 'semantic-ui-react';
import SeatMap from '@weezevent/throne';
import PartnerAvailability from '../../models/partnerAvailability';
import CardLoader from '../../components/cardLoader';
import IconPlus from '@weezevent/throne/dist/images/icon-plus.svg';
import IconMinus from '@weezevent/throne/dist/images/icon-minus.svg';
import { NoItems } from '../../components/no-items';
import Zone from '../../models/zoneModel';

export function prepare_seats(zone) {
    const new_seats = zone.seats.map(seat => {
        seat.available = seat.booking_state === 'AVAILABLE';
        return seat;
    });

    return new_seats;
}

function NumberInputSeat({ onChange, disabled, value, max = 10000 }) {
    return <Counter placeholder={0} onChange={onChange} max={max} disabled={disabled} value={value} min={0} />;
}
NumberInputSeat.propTypes = {
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
    value: PropTypes.number.isRequired,
    max: PropTypes.number
};

export function SeatStyles({ seats, selectedSeats, availableColor = 'blue', selectedColor = 'green' }) {
    let seat_styles = seats.map(seat => {
        let opacity = seat.available ? 1 : 0.2;
        return `td[data-seat-id="${seat.uuid}"] svg path {
			fill: ${availableColor};
			opacity: ${opacity};
		}

		td[data-seat-id="${seat.uuid}"] {
			cursor: ${availableColor};
		}
		td[data-seat-id="${seat.uuid}"]:hover svg path {
			fill: ${availableColor} !important;
		}`;
    });

    // Set selected seats styles
    const styles = selectedSeats.map(seat => {
        return `
			td[data-seat-id="${seat.uuid}"] svg path {
				fill: ${selectedColor};
				opacity: 0.6;
			}
		`;
    });

    return <style>{seat_styles.concat(styles)}</style>;
}

SeatStyles.propTypes = {
    seats: PropTypes.array,
    selectedSeats: PropTypes.array,
    availableColor: PropTypes.string,
    selectedColor: PropTypes.string
};

export function SeatSelector({ zoneId, onSeatsSelected = () => {}, selectedSeats = [], inventories, rates, partnerId, organization }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const query = 'inventories=' + inventories.map(x => x.uuid).join(',') + '&rate=' + rates.map(rate => rate.pk).join(',');
    const [zone, setZone] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    React.useEffect(() => {
        setLoading(true);
        PartnerAvailability.getAll(
            {
                organizationId: organization.id,
                partnerId,
                zoneId
            },
            query
        )
            .then(availability => {
                availability.seats = prepare_seats(availability);
                setZone(availability);
                setLoading(false);
            })
            .catch(e => {
                toast.error(t('ticket.partners.batch.seat.availability_error'));
            });
    }, [zoneId]);

    if (loading) {
        return <CardLoader />;
    }

    if (!zone) {
        return null;
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>{zone.name}</div>
                {selectedSeats.length > 0 && <div>{t('ticket.partners.batch.seat.count_selected_seats', { count: selectedSeats.length || 0 })}</div>}
            </div>
            <SeatStyles seats={zone.seats} selectedSeats={selectedSeats} />
            <SeatMap
                key={zone.uuid}
                zone={zone}
                draggable={true}
                onSeatsSelected={onSeatsSelected}
                onSeatClicked={seat => onSeatsSelected([seat])}
                mapOptions={{ zoomControls: { plus: IconPlus, minus: IconMinus }, showRowNumbers: true }}
            />
        </>
    );
}
SeatSelector.propTypes = {
    zoneId: PropTypes.string,
    onSeatsSelected: PropTypes.func,
    selectedSeats: PropTypes.array,
    rates: PropTypes.array,
    inventories: PropTypes.array,
    organization: PropTypes.object,
    partnerId: PropTypes.string
};

/**
 * Display icon eye with onclick function in manualCase, or the quantity selected for autobooking.
 * @param {*} param0
 */
function DisplayEyeButton({ autoBooking, is_final, child, zoneDisplayed, handleDisplaySeatSelector, quantitySelectedInChildren, quantitySelected, maxNumber, maxNumberChild }) {
    const { t } = useTranslation();
    if (!autoBooking) {
        if (is_final) {
            return <Icon onClick={() => handleDisplaySeatSelector(child)} name={zoneDisplayed === child.uuid ? 'eye slash' : 'eye'} />;
        } else {
            return null;
        }
    }

    if (is_final) {
        return (
            <div style={{ float: 'right' }}>
                {`${t('ticket.partners.batch.seat.seat_selected_auto', {
                    count: quantitySelectedInChildren(child.uuid) !== 0 ? quantitySelectedInChildren(child.uuid) : quantitySelected(child.uuid)
                })} / ${maxNumber(child)}`}
            </div>
        );
    }

    return (
        <div style={{ float: 'right' }}>{`${t('ticket.partners.batch.seat.seat_selected_auto', {
            count: quantitySelected(child.uuid) !== 0 ? quantitySelected(child.uuid) : quantitySelectedInChildren(child.uuid)
        })} / ${maxNumberChild(child)}`}</div>
    );
}
DisplayEyeButton.propTypes = {
    autoBooking: PropTypes.bool.isRequired,
    is_final: PropTypes.bool.isRequired,
    child: PropTypes.object.isRequired,
    zoneDisplayed: PropTypes.string,
    maxNumberChild: PropTypes.func.isRequired,
    handleDisplaySeatSelector: PropTypes.func.isRequired,
    quantitySelectedInChildren: PropTypes.func.isRequired,
    quantitySelected: PropTypes.func.isRequired,
    maxNumber: PropTypes.func.isRequired
};

export function ZoneRow({
    zone = null,
    partnerId,
    depth = 0,
    selectedSeats = [],
    rates,
    inventories,
    zoneDisplayed,
    onDisplaySeatSelector,
    autoBooking,
    onReset,
    onChange,
    autoBookingQuantity
}) {
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const [children, setChildren] = React.useState({});
    const [childrenDisplayed, setChildrenDisplayed] = React.useState([]);
    const [zoneIdLoading, setZoneIdLoading] = React.useState(null);
    const query = 'inventories=' + inventories.map(x => x.uuid).join(',') + '&rate=' + rates.map(rate => rate.pk).join(',');

    /**
     * AUTOBOOKING : Get number of availability seats in zone
     */
    const maxNumber = React.useCallback(child => {
        if (child?.categories) {
            let tmpCount = child.categories.map(category => category.available).reduce((a, b) => a + b, 0);
            return tmpCount;
        }
        return 0;
    }, []);

    /**
     * AUTOBOOKING : Get number of autobooking selected in children of zone
     */
    const quantitySelectedInChildren = React.useCallback(
        zone_uuid => {
            if (children[zone_uuid]) {
                let countInChild = children[zone_uuid].children
                    .map(child => {
                        let index_zone = autoBookingQuantity.findIndex(o => o.zone === child.uuid);
                        if (index_zone !== -1) {
                            return autoBookingQuantity[index_zone].quantity;
                        }
                        return 0;
                    })
                    .reduce((a, b) => a + b, 0);
                return countInChild;
            }
            return 0;
        },
        [children, autoBookingQuantity]
    );

    /**
     * AUTOBOOKING : Get number of autobooking selected in this zone
     */
    const quantitySelected = React.useCallback(
        zone_uuid => {
            let index_zone = autoBookingQuantity.findIndex(o => o.zone === zone_uuid);
            if (index_zone !== -1) {
                return autoBookingQuantity[index_zone].quantity;
            }
            return 0;
        },
        [autoBookingQuantity]
    );

    /**
     * AUTOBOOKING : Get availability seats for the child displayed
     */
    const maxNumberChild = React.useCallback(child => {
        return child.categories.map(category => category.available).reduce((a, b) => a + b, 0);
    }, []);

    const zoneSeatsCount = React.useMemo(() => {
        let count = {};
        if (!zone) {
            return count;
        }
        zone.children.forEach(child => {
            count[child.uuid] = selectedSeats.filter(seat => seat.zone === child.uuid).length;
        });
        return count;
    }, [selectedSeats, zone]);

    const fetchAvailability = React.useCallback(
        id => {
            setZoneIdLoading(id);
            PartnerAvailability.getAll(
                {
                    organizationId: organization.id,
                    partnerId: partnerId,
                    zoneId: id
                },
                query
            )
                .then(availability => {
                    let tmpChildren = { ...children };
                    tmpChildren[id] = availability;
                    setChildren({ ...tmpChildren });
                })
                .catch(e => {
                    toast.error(t('ticket.partners.batch.seat.availability_error'));
                })
                .finally(() => {
                    setZoneIdLoading(null);
                });
        },
        [partnerId, organization, children]
    );

    const displayChild = React.useCallback(
        id => {
            //Check if we have to hide or display the child
            if (childrenDisplayed.includes(id)) {
                let tmpChildren = childrenDisplayed.filter(_id => _id !== id);
                return setChildrenDisplayed([...tmpChildren]);
            }
            onReset([id]);
            let tmpChildren = [...childrenDisplayed];
            tmpChildren.push(id);
            setChildrenDisplayed(tmpChildren);
            return children[id] || fetchAvailability(id);
        },
        [fetchAvailability, children, childrenDisplayed, onReset]
    );

    const handleDisplaySeatSelector = React.useCallback(
        zone => {
            onDisplaySeatSelector(zoneDisplayed === zone?.uuid ? null : zone);
        },
        [onDisplaySeatSelector, zoneDisplayed]
    );

    // Unfold zone if one child
    React.useEffect(() => {
        if (zone?.children.length === 1) {
            if (autoBooking) {
                handleDisplaySeatSelector(null);
                setChildrenDisplayed([]);
            } else {
                if (!zone.children[0]?.seatlib_data?.is_terminal) {
                    displayChild(zone.children[0].uuid);
                } else {
                    handleDisplaySeatSelector(zone.children[0]);
                }
            }
        }
    }, [zone, autoBooking, setChildrenDisplayed]);

    const onClickRow = React.useCallback(
        (is_final, child, disabledTrigger = false) => {
            if (!child) {
                return;
            }

            if (!autoBooking) {
                if (!is_final) {
                    return displayChild(child.uuid);
                } else {
                    return handleDisplaySeatSelector(child);
                }
            }
            if (disabledTrigger) {
                // To force disable onClick on 2nd column in autobooking because we have to click on Counter component
                return;
            }

            if (!is_final) {
                displayChild(child.uuid);
            }
        },
        [autoBooking, displayChild, handleDisplaySeatSelector]
    );

    if (!zone && !zoneDisplayed) {
        return null;
    }

    if (!zone && zoneDisplayed) {
        // Hack to not return error and let possibility to select zone and goes to Manual selection.
        return <> </>;
    }

    return (
        <>
            {zone.children.map(child => {
                const is_final = Boolean(child?.seatlib_data?.is_terminal);
                return (
                    <>
                        <TableRow key={child.uuid}>
                            <TableCell onClick={() => onClickRow(is_final, child)}>
                                {!is_final && <Icon name={childrenDisplayed.includes(child.uuid) ? 'angle down' : 'angle right'} onClick={() => displayChild(child.uuid)} />}
                                {child.name}
                                {Boolean(zoneIdLoading === child.uuid) && <Loader size="tiny" style={{ left: '1rem', zIndex: 1 }} inline />}
                            </TableCell>
                            <TableCell onClick={() => onClickRow(is_final, child, true)}>
                                {!autoBooking ? (
                                    is_final && <span>{zoneSeatsCount[child.uuid] || 0}</span>
                                ) : (
                                    // Autobooking case
                                    <NumberInputSeat
                                        onChange={(event, data) => onChange(child.uuid, parseInt(data))}
                                        max={!is_final ? maxNumber(child) : maxNumberChild(child)}
                                        disabled={childrenDisplayed.includes(child.uuid) || quantitySelectedInChildren(child.uuid) !== 0}
                                        value={quantitySelectedInChildren(child.uuid) !== 0 ? quantitySelectedInChildren(child.uuid) : quantitySelected(child.uuid)}
                                    />
                                )}
                            </TableCell>
                            <TableCell style={{ width: '33%' }} onClick={() => onClickRow(is_final, child)}>
                                <DisplayEyeButton
                                    autoBooking={autoBooking}
                                    child={child}
                                    zoneDisplayed={zoneDisplayed}
                                    maxNumberChild={maxNumberChild}
                                    is_final={is_final}
                                    handleDisplaySeatSelector={handleDisplaySeatSelector}
                                    quantitySelectedInChildren={quantitySelectedInChildren}
                                    quantitySelected={quantitySelected}
                                    maxNumber={maxNumber}
                                />
                            </TableCell>
                        </TableRow>
                        {Object.keys(children)
                            .filter(childId => childrenDisplayed.includes(childId) && childId === child.uuid)
                            .map(childId => {
                                let _child = children[childId];
                                return (
                                    <ZoneRow
                                        key={_child.uuid}
                                        zone={_child}
                                        depth={depth + 1}
                                        partnerId={partnerId}
                                        inventories={inventories}
                                        rates={rates}
                                        onDisplaySeatSelector={onDisplaySeatSelector}
                                        zoneDisplayed={zoneDisplayed}
                                        selectedSeats={selectedSeats}
                                        autoBooking={autoBooking}
                                        onReset={onReset}
                                        onChange={onChange}
                                        autoBookingQuantity={autoBookingQuantity}
                                    />
                                );
                            })}
                    </>
                );
            })}
        </>
    );
}

ZoneRow.propTypes = {
    zone: PropTypes.object.isRequired,
    partnerId: PropTypes.string.isRequired,
    depth: PropTypes.number,
    selectedSeats: PropTypes.object,
    rates: PropTypes.array,
    inventories: PropTypes.array,
    zoneDisplayed: PropTypes.string,
    onDisplaySeatSelector: PropTypes.func,
    autoBooking: PropTypes.bool.isRequired,
    onReset: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    autoBookingQuantity: PropTypes.array.isRequired
};

function GroupButton({ children }) {
    return <div>{children}</div>;
}
GroupButton.propTypes = {
    children: PropTypes.node
};

function TableHeaderBooking({ autoBooking }) {
    const { t } = useTranslation();
    if (autoBooking) {
        return (
            <TableHeader>
                <TableHeaderCell>{t('ticket.partners.batch.seat.zone')}</TableHeaderCell>
                <TableHeaderCell>{t('ticket.partners.batch.seat.seat_auto')}</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
            </TableHeader>
        );
    }
    return (
        <TableHeader>
            <TableHeaderCell>{t('ticket.partners.batch.seat.zone')}</TableHeaderCell>
            <TableHeaderCell>{t('ticket.partners.batch.seat.seat')}</TableHeaderCell>
            <TableHeaderCell>{t('ticket.partners.batch.seat.action')}</TableHeaderCell>
        </TableHeader>
    );
}
TableHeaderBooking.propTypes = {
    autoBooking: PropTypes.bool.isRequired
};

function AutoBookingTotal({ zone = null, autoBookingQuantity }) {
    const { t } = useTranslation();
    const maxNumber = React.useMemo(() => {
        if (zone?.children) {
            let count = zone.children
                .map(child => {
                    let tmpCount = child.categories.map(category => category.available).reduce((a, b) => a + b, 0);
                    return tmpCount;
                })
                .reduce((a, b) => a + b, 0);
            return count;
        }
        return 0;
    }, [zone]);

    return (
        <TableRow key={'total'}>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell style={{ width: '33%' }}>
                <div style={{ float: 'right' }}>
                    {t('ticket.partners.batch.seat.total', {
                        taken: autoBookingQuantity.map(zone => zone.quantity).reduce((a, b) => a + b, 0),
                        available: maxNumber
                    })}
                </div>
            </TableCell>
        </TableRow>
    );
}
AutoBookingTotal.propTypes = {
    zone: PropTypes.object.isRequired,
    autoBookingQuantity: PropTypes.array.isRequired
};

/**
 * This component is used for manual booking and for autobooking
 * @param {*} param0
 */
function Bookings({
    partnerId,
    availability,
    rates,
    inventories,
    displayedZone,
    setDisplayedZoneId,
    selectedSeats,
    organization,
    handleSeatsSelected,
    autoBooking,
    setAutoBookingQuantity,
    autoBookingQuantity
}) {
    /**
     * Reset function for autobooking
     */
    const onReset = React.useCallback(
        uuid_array => {
            let newAutoBooking = autoBookingQuantity.slice(0);
            for (let uuid of uuid_array) {
                let index_zone = newAutoBooking.findIndex(o => o.zone === uuid);
                if (index_zone !== -1) {
                    newAutoBooking.splice(index_zone, 1);
                }
            }
            setAutoBookingQuantity(newAutoBooking);
        },
        [autoBookingQuantity, setAutoBookingQuantity]
    );

    /**
     * OnChange function for autobooking
     */
    const onChange = React.useCallback((uuid, value) => {
        let index_zone = autoBookingQuantity.findIndex(o => o.zone === uuid);
        let newAutoBooking = autoBookingQuantity.slice(0);

        if (index_zone === -1) {
            if (value === 0) {
                return;
            }
            newAutoBooking.push({ zone: uuid, quantity: parseInt(value) });
            setAutoBookingQuantity(newAutoBooking);
        } else {
            if (value === 0) {
                // Delete object if == 0
                let newAutoBooking = autoBookingQuantity.slice(0);
                newAutoBooking.splice(index_zone, 1);
                setAutoBookingQuantity(newAutoBooking);
            } else {
                let object = autoBookingQuantity[index_zone];
                let newQuantity = parseInt(value);

                object.quantity = newQuantity;
                newAutoBooking[index_zone] = object;
                setAutoBookingQuantity(newAutoBooking);
            }
        }
    });

    return (
        <React.Fragment>
            <Table>
                <TableHeaderBooking autoBooking={autoBooking} />
                <TableBody>
                    <ZoneRow
                        partnerId={partnerId}
                        zone={availability}
                        rates={rates}
                        inventories={inventories}
                        zoneDisplayed={displayedZone ? displayedZone.uuid : null}
                        onDisplaySeatSelector={setDisplayedZoneId}
                        selectedSeats={selectedSeats}
                        // autobooking params
                        autoBooking={autoBooking}
                        onReset={onReset}
                        onChange={onChange}
                        autoBookingQuantity={autoBookingQuantity}
                    />
                    {autoBooking && <AutoBookingTotal zone={availability} autoBookingQuantity={autoBookingQuantity} />}
                </TableBody>
            </Table>
            {!autoBooking && displayedZone && (
                <SeatSelector
                    zoneId={displayedZone.uuid}
                    inventories={inventories}
                    rates={rates}
                    partnerId={partnerId}
                    organization={organization}
                    onSeatsSelected={handleSeatsSelected}
                    selectedSeats={selectedSeats}
                />
            )}
        </React.Fragment>
    );
}
Bookings.propTypes = {
    partnerId: PropTypes.string,
    availability: PropTypes.object.isRequired,
    rates: PropTypes.array,
    inventories: PropTypes.array,
    displayedZone: PropTypes.string,
    setDisplayedZoneId: PropTypes.func.isRequired,
    selectedSeats: PropTypes.array.isRequired,
    organization: PropTypes.array,
    handleSeatsSelected: PropTypes.func.isRequired,
    autoBooking: PropTypes.bool.isRequired,
    setAutoBookingQuantity: PropTypes.func.isRequired,
    autoBookingQuantity: PropTypes.array.isRequired
};

export default function PartnerSeat({
    partnerId,
    rates,
    rate_category_is_ga,
    inventories,
    onSeatsSelected = () => {},
    autoBooking,
    setAutoBooking,
    setAutoBookingQuantity,
    autoBookingQuantity
}) {
    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const [selectedSeats, setSelectedSeats] = React.useState([]);
    const [displayedZone, setDisplayedZoneId] = React.useState(null);
    const query = 'inventories=' + inventories.map(x => x.uuid).join(',') + '&rate=' + rates.map(rate => rate.pk).join(',');
    const [displayZoneChoice, setDisplayZoneChoice] = React.useState(false);
    const [availability, loading] = PartnerAvailability.useApiModel({
        organizationId: organization.id,
        launch: Boolean(inventories.length && rates.length),
        defaultLoading: Boolean(inventories.length && rates.length),
        partnerId,
        query,
        // We expect some errors from the server and the page should
        // not crash when they happen.
        strict: false,
        onError: () => {
            toast.error(t('ticket.partners.batch.seat.availability_error'));
            setDisplayZoneChoice(true);
        }
    });

    const chartId = inventories[0].chart_id;
    const [zones] = Zone.useApiModel({
        organizationId: organization.id,
        chartId: chartId
    });

    const zoneOptions = React.useMemo(() => {
        if (!zones) {
            return [];
        }
        if (!zones.length) {
            return [];
        }
        let zoneTab = zones.map(zone => {
            return { text: zone.name, value: zone };
        });

        return zoneTab;
    }, [zones]);

    const selectSeats = React.useCallback(
        seats => {
            setSelectedSeats(seats);
            onSeatsSelected(seats);
        },
        [onSeatsSelected]
    );

    const handleSeatsSelected = React.useCallback(
        seats => {
            seats = seats.filter(s => s.available);
            if (!seats?.length) {
                return null;
            }
            //We just clicked on one seat
            if (seats.length === 1) {
                let hasBeenDeleted = false;
                let newSelectedSeats = selectedSeats.filter(seat => {
                    if (seat.uuid !== seats[0].uuid) {
                        return true;
                    }
                    hasBeenDeleted = true;
                    return false;
                });
                if (hasBeenDeleted) {
                    return selectSeats([...newSelectedSeats]);
                }
                return selectSeats([...newSelectedSeats, ...seats]);
            }
            //We try to add several seats
            //First filter to be sure we don't try to add seats already added
            seats = seats.filter(seat => !selectedSeats.find(s => s.uuid === seat.uuid));
            return selectSeats([...selectedSeats, ...seats]);
        },
        [selectSeats, selectedSeats]
    );

    if (loading) {
        return <CardLoader />;
    }

    if (availability?.children?.length === 0 && !displayZoneChoice) {
        return <NoItems title={t('ticket.partners.batch.no_zones')} />;
    }

    return (
        <div>
            {!rate_category_is_ga && (
                <Form.Field>
                    <Form.Label label={t('ticket.partners.batch.rate.seatingSelection')} />
                    <div>
                        <RadioLabel
                            inline
                            bordered
                            active={autoBooking}
                            name="autoBooking"
                            checked={autoBooking}
                            label={t('ticket.partners.batch.rate.autoSeating')}
                            onClick={() => setAutoBooking(true)}
                        />
                        <RadioLabel
                            inline
                            bordered
                            active={!autoBooking}
                            name="manualBooking"
                            checked={!autoBooking}
                            label={t('ticket.partners.batch.rate.manualSeating')}
                            onClick={() => setAutoBooking(false)}
                        />
                    </div>
                </Form.Field>
            )}
            {displayZoneChoice && (
                <Form.Field>
                    <Form.Label label={t('ticket.partners.batch.rate.seatingZoneSelection')} />
                    <div>
                        <Select
                            placeholder={t('ticket.partners.batch.rate.seatingZoneSelectionplaceHolder')}
                            clearable={true}
                            disabled={!zones}
                            options={zoneOptions}
                            value={displayedZone}
                            onChange={(_, { value }) => {
                                setDisplayedZoneId(value);
                            }}
                        />
                    </div>
                </Form.Field>
            )}
            <Bookings
                partnerId={partnerId}
                availability={availability}
                rates={rates}
                inventories={inventories}
                displayedZone={displayedZone}
                setDisplayedZoneId={setDisplayedZoneId}
                selectedSeats={selectedSeats}
                organization={organization}
                handleSeatsSelected={handleSeatsSelected}
                autoBooking={autoBooking}
                setAutoBookingQuantity={setAutoBookingQuantity}
                autoBookingQuantity={autoBookingQuantity}
            />
        </div>
    );
}

PartnerSeat.propTypes = {
    partnerId: PropTypes.string,
    rates: PropTypes.array,
    rate_category_is_ga: PropTypes.bool.isRequired,
    inventories: PropTypes.array,
    onSeatsSelected: PropTypes.func,
    autoBooking: PropTypes.bool.isRequired,
    setAutoBooking: PropTypes.func.isRequired,
    setAutoBookingQuantity: PropTypes.func.isRequired,
    autoBookingQuantity: PropTypes.array.isRequired
};
